/**
 * Mastodon API client - Statuses endpoints
 */

import { get, post, put, del, buildQuery } from "./http";
import type {
	Status,
	StatusContext,
	StatusSource,
	CreateStatusParams,
	EditStatusParams,
	Account,
} from "./types";

/**
 * Get a status by ID
 */
export async function getStatus(id: string): Promise<Status> {
	return get<Status>(`/api/v1/statuses/${id}`);
}

/**
 * Get the context (ancestors and descendants) of a status
 */
export async function getContext(id: string): Promise<StatusContext> {
	return get<StatusContext>(`/api/v1/statuses/${id}/context`);
}

/**
 * Get the source of a status (for editing)
 */
export async function getSource(id: string): Promise<StatusSource> {
	return get<StatusSource>(`/api/v1/statuses/${id}/source`);
}

/**
 * Get accounts who favourited the status
 */
export async function getFavouritedBy(id: string): Promise<Account[]> {
	return get<Account[]>(`/api/v1/statuses/${id}/favourited_by`);
}

/**
 * Get accounts who reblogged the status
 */
export async function getRebloggedBy(id: string): Promise<Account[]> {
	return get<Account[]>(`/api/v1/statuses/${id}/reblogged_by`);
}

/**
 * Create a new status
 */
export async function createStatus(params: CreateStatusParams): Promise<Status> {
	return post<Status>("/api/v1/statuses", params);
}

/**
 * Edit an existing status
 */
export async function editStatus(id: string, params: EditStatusParams): Promise<Status> {
	return put<Status>(`/api/v1/statuses/${id}`, params);
}

/**
 * Delete a status
 */
export async function deleteStatus(id: string): Promise<Status> {
	return del<Status>(`/api/v1/statuses/${id}`);
}

/**
 * Favourite a status
 */
export async function favourite(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/favourite`);
}

/**
 * Unfavourite a status
 */
export async function unfavourite(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/unfavourite`);
}

/**
 * Reblog (boost) a status
 */
export async function reblog(
	id: string,
	options?: { visibility?: "public" | "unlisted" | "private" },
): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/reblog`, options);
}

/**
 * Undo a reblog
 */
export async function unreblog(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/unreblog`);
}

/**
 * Bookmark a status
 */
export async function bookmark(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/bookmark`);
}

/**
 * Remove a bookmark
 */
export async function unbookmark(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/unbookmark`);
}

/**
 * Mute a conversation
 */
export async function muteConversation(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/mute`);
}

/**
 * Unmute a conversation
 */
export async function unmuteConversation(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/unmute`);
}

/**
 * Pin a status to profile
 */
export async function pin(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/pin`);
}

/**
 * Unpin a status from profile
 */
export async function unpin(id: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/unpin`);
}

/**
 * React to a status with an emoji (Pleroma/Akkoma extension)
 */
export async function react(id: string, emoji: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/react/${encodeURIComponent(emoji)}`);
}

/**
 * Remove a reaction from a status (Pleroma/Akkoma extension)
 */
export async function unreact(id: string, emoji: string): Promise<Status> {
	return post<Status>(`/api/v1/statuses/${id}/unreact/${encodeURIComponent(emoji)}`);
}

/**
 * Translate a status (Mastodon 4.0+)
 */
export async function translate(
	id: string,
	lang?: string,
): Promise<{ content: string; detected_source_language: string; provider: string }> {
	const body = lang ? { lang } : undefined;
	return post(`/api/v1/statuses/${id}/translate`, body);
}

export const statuses = {
	get: getStatus,
	getContext,
	getSource,
	getFavouritedBy,
	getRebloggedBy,
	create: createStatus,
	edit: editStatus,
	delete: deleteStatus,
	favourite,
	unfavourite,
	reblog,
	unreblog,
	bookmark,
	unbookmark,
	muteConversation,
	unmuteConversation,
	pin,
	unpin,
	react,
	unreact,
	translate,
};
